import { useMutation } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { PinLoginRequest } from '@/api/handlers/resources/auth/requests';
import type { PinLoginResponse } from '@/api/handlers/resources/auth/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { setSession } from '@/utils';

/**
 * Mutation behind the PIN pad. On success it persists the seller session (the
 * per-shift access + refresh token and the user) so subsequent catalog/sales
 * calls are authorized. A wrong PIN / lockout / unknown user / rate limit comes
 * back as a `success: false` envelope — the caller classifies it by its code
 * (`PIN_LOCKED`, `RATE_LIMITED`) or as a generic "invalid PIN" (see
 * `components/pinPad/usePinAttemptError.ts`).
 */
export const usePinLoginMutation = () => {
    const { apiClient } = useApi();

    return useMutation<ApiResponse<PinLoginResponse>, Error, PinLoginRequest>({
        mutationFn: (body) => apiClient.auth.pinLogin(body),
        onSuccess: (result) => {
            if (result.success) {
                setSession(result.response);
            }
        },
    });
};
